import type {
  PremadeCharacter,
  PremadeSourceContractWithRuleset,
} from "@dndbeyond/character-rules-engine";
import type { Option } from "@dndbeyond/ttui/lexicon/components/Dropdown";
import type { TabFilterOption } from "@dndbeyond/ttui/lexicon/components/TabFilter";

import { ALL_RULESETS } from "../constants";
import { compareRulesets } from "./compareRulesets";
import { getPrimarySource } from "./getPrimarySource";

// A Collection dropdown option, grouped under (and filterable by) its ruleset
export interface CollectionOption extends Option {
  rulesetId: number;
}

export interface PremadeFilterOptions {
  classes: Array<Option>;
  species: Array<Option>;
  levels: Array<Option>;
  sources: Array<CollectionOption>;
  rulesets: Array<TabFilterOption>;
}

/**
 * Builds the filter dropdown and Rule Set tab options from the premades.
 */
export const getFilterOptions = (
  premades: Array<PremadeCharacter>
): PremadeFilterOptions => {
  // Extract unique classes and sort by name
  const classes = Array.from(
    new Set(premades.map((char) => char.characterData.primaryClassName))
  )
    .sort((a, b) => a.localeCompare(b))
    .map((className) => ({ label: className, value: className }));

  // Extract unique species and sort by name
  const species = Array.from(
    new Set(premades.map((char) => char.characterData.speciesName))
  )
    .sort((a, b) => a.localeCompare(b))
    .map((speciesName) => ({ label: speciesName, value: speciesName }));

  // Extract unique levels (numeric so "Level 10" follows "Level 9")
  const levels = Array.from(
    new Set(premades.map((char) => char.characterData.characterLevel))
  )
    .sort((a, b) => a - b)
    .map((level) => ({ label: `Level ${level}`, value: level.toString() }));

  // Extract unique sources (deduplicate by sourceId). A source only ever
  // belongs to one ruleset, so the first one seen has the right ruleset.
  const sourcesMap = new Map<number, PremadeSourceContractWithRuleset>();
  premades.forEach((char) => {
    char.sources.forEach((source) => {
      if (!sourcesMap.has(source.sourceId)) {
        sourcesMap.set(source.sourceId, source);
      }
    });
  });

  // Group sources by ruleset in the same order as the Rule Set tabs,
  // alphabetized within each group. The Dropdown orders its groups by where
  // each group first appears in the options.
  const sources = Array.from(sourcesMap.values())
    .sort(
      (a, b) =>
        compareRulesets(a.rulesetId, b.rulesetId) ||
        a.sourceName.localeCompare(b.sourceName)
    )
    .map(({ sourceId, sourceName, rulesetId, rulesetDisplayLabel }) => ({
      label: sourceName,
      value: sourceId.toString(),
      groupName: rulesetDisplayLabel,
      rulesetId,
    }));

  // Extract unique rulesets from primary sources, skipping unsourced characters
  const rulesetsMap = new Map<number, string>();
  premades.forEach((char) => {
    const { rulesetId, rulesetDisplayLabel } = getPrimarySource(char);
    if (rulesetId && !rulesetsMap.has(rulesetId)) {
      rulesetsMap.set(rulesetId, rulesetDisplayLabel);
    }
  });

  // Rulesets in RULESET_ORDER, after "All"
  const rulesets = [
    { label: "All", value: ALL_RULESETS },
    ...Array.from(rulesetsMap.entries())
      .sort((a, b) => compareRulesets(a[0], b[0]))
      .map(([rulesetId, label]) => ({ label, value: rulesetId })),
  ];

  return { classes, species, levels, sources, rulesets };
};
